import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { postData } from '../../../utils/postData';
import StepWizard from '../../shared/StepWizard';
import GoBackRedirect from '../../shared/GoBackRedirect';
import { RevokeCertificateContext, RevokeCertificateDispatchContext } from './context';
import ConfirmRevoke from '../../cardLibrary/ConfirmRevoke';
import SelectReason from '../../cardLibrary/SelectReason';
import PerformAction from '../../cardLibrary/PerformAction';
import {
    AppDispatchContext,
    AppStateContext,
} from '../../../context/AppContext';
import Status from '../../cardLibrary/Status';
import {
    serverConstants,
    paths,
    credentialEndpoint,
    PROGRESS_BAR_DONE_DELAY,
} from '../../../constants';
import {
    HelpDeskDispatchContext,
} from '../../../context/HelpDeskContext';
import { useLazyPatchData } from '../../../utils/hooks';
 
const CONFIRM_REVOKE = 'confirmRevoke';
const PERFORM_ACTION = 'performAction';
const STATUS = 'status';
const HOME = 'home';
 
function reducer(state, action) {
    switch (action.type) {
        case 'DONE':
            return { step: HOME };
        case 'ON_SUCCESS':
            return {
                ...state,
                statusMessage: 'revokeCertificateSuccessMsg',
                errorMessage: false,
                loading: false,
                step: STATUS,
            };
        case 'ON_FAILURE':
            return {
                ...state,
                loading: false,
                statusMessage: action.payload,
                errorMessage: true,
                step: STATUS,
            };
        case 'GO_BACK':
            if (state.step === CONFIRM_REVOKE) {
                return { ...state, step: HOME };
            }
            return state;
        case 'GO_NEXT':
            if (state.step === CONFIRM_REVOKE) {
                return { ...state, step: PERFORM_ACTION, loading: true  };
            }
            return state;
        case 'PROGRESS_BAR_DONE':
            return { ...state, isDone: true };
        default:
            throw new Error('RevokeCertificate Device Reducer: invalid action');
    }
}
 
const RevokeCertificate = () => {
    const { device, goBackLink, credential } = useContext(AppStateContext);
    const appDispatch = useContext(AppDispatchContext);
    const helpDeskDispatch = useContext(HelpDeskDispatchContext);
    const [state, dispatch] = useReducer(reducer, {
        step: CONFIRM_REVOKE,
        nextStep: PERFORM_ACTION,
        prevStep: HOME,
        statusMessage: null,
        errorMessage: null,
        loading: false,
        isDone: false,
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { type, user } = device || {};
    const { pathname } = location || {};
    const isHelpDesk = pathname ? pathname.includes(paths.helpDesk) : false;
    const returnPath =
        goBackLink || (isHelpDesk ? paths.helpDesk : paths.identities);
 
    const [patchCallback] = useLazyPatchData({
        onSuccess:  async (res) => {        
            const status = res.status;
            if (["REVOKED", "REVOKE_PENDING"].includes(status)) {
                dispatch({ type: 'PROGRESS_BAR_DONE' });
                setTimeout(
                    () => dispatch({ type: 'ON_SUCCESS', payload: null }),
                    PROGRESS_BAR_DONE_DELAY,
                );
            } else {
                dispatch({ type: 'PROGRESS_BAR_DONE' });
                setTimeout(
                    () =>
                        dispatch({
                            type: 'ON_FAILURE',
                            payload: status || t('errorMsgs$serverDefault'),
                        }),
                    PROGRESS_BAR_DONE_DELAY,
                );
            }
        },
        onError: async (res) => {
            dispatch({ type: 'PROGRESS_BAR_DONE' });
            setTimeout(
                () =>
                    dispatch({
                        type: 'ON_FAILURE',
                        payload: t('errorMsgs$serverDefault'),
                    }),
                PROGRESS_BAR_DONE_DELAY,
            );    
        },
    });
  
    const performAction = useCallback(async () => {
        patchCallback({
            endpoint: `${credentialEndpoint}/${credential.type}/${credential.identifier}`,
            body: {
                status: "REVOKED",
                user: user || "",
                provider: credential.provider,
            },
        });
    }, [credential, user]);
 
    useEffect(() => {
        if (state.step === PERFORM_ACTION) {
            performAction();
        }
    }, [state.step, performAction]);
 
    useEffect(() => {
        return () => {
            helpDeskDispatch({ type: 'RESET_CONTEXT' });
            appDispatch({ type: 'RESET_FLOWS' });
        };
    },[]);
 
    return (
        <RevokeCertificateContext.Provider value={state}>
            <RevokeCertificateDispatchContext.Provider value={dispatch}>
                <StepWizard
                    step={state.step}
                    title={t('cardTitles$revokeDevice')}
                    dispatch={dispatch}
                    state={state}
                >
                    <GoBackRedirect
                        path={returnPath}
                        stepId={HOME}
                    />
                    <ConfirmRevoke
                        stepId={CONFIRM_REVOKE}
                    />
                    <PerformAction
                        stepId={PERFORM_ACTION}
                        isDone={state.isDone}
                    />
                    <Status path={returnPath} stepId={STATUS} />
                </StepWizard>
            </RevokeCertificateDispatchContext.Provider>
        </RevokeCertificateContext.Provider>
    );
};
 
export default RevokeCertificate;
 